import React, { useState, useEffect } from 'react';
import { League, Sport, Country } from '../../types/sports';

interface LeagueFormProps {
  league?: League | null;
  sports: Sport[];
  countries: Country[];
  onSave: (data: {
    name: string;
    shortName?: string;
    description?: string;
    level?: number;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const LeagueForm: React.FC<LeagueFormProps> = ({
  league,
  sports,
  countries,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    description: '',
    level: 1,
    isActive: true,
    sportId: 0,
    countryId: 0
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (league) {
      setFormData({
        name: league.name || '',
        shortName: league.shortName || '',
        description: league.description || '',
        level: league.level || 1,
        isActive: league.isActive ?? true,
        sportId: league.sportId || 0,
        countryId: league.countryId || 0
      });
    }
  }, [league]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name muss mindestens 2 Zeichen lang sein';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name darf maximal 100 Zeichen lang sein';
    }

    if (formData.shortName && formData.shortName.length > 10) {
      newErrors.shortName = 'Kurzname darf maximal 10 Zeichen lang sein';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Beschreibung darf maximal 500 Zeichen lang sein';
    }

    if (formData.level < 1 || formData.level > 10) {
      newErrors.level = 'Level muss zwischen 1 und 10 liegen';
    }

    if (formData.sportId === 0) {
      newErrors.sportId = 'Sportart ist erforderlich';
    }

    if (formData.countryId === 0) {
      newErrors.countryId = 'Land ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || undefined,
        description: formData.description.trim() || undefined,
        level: formData.level,
        isActive: formData.isActive,
        sportId: formData.sportId,
        countryId: formData.countryId
      };
      onSave(submitData);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '16px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '500',
    color: '#374151'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const errorInputStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: '#ef4444'
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const errorStyle: React.CSSProperties = {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#3b82f6',
    color: 'white'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151'
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px'
  };

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <h3 style={{ marginTop: 0, marginBottom: '24px', color: '#1f2937' }}>
        {league ? 'Liga bearbeiten' : 'Neue Liga erstellen'}
      </h3>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          style={errors.name ? errorInputStyle : inputStyle}
          placeholder="Liga-Name eingeben"
          disabled={loading}
        />
        {errors.name && <div style={errorStyle}>{errors.name}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Kurzname</label>
        <input
          type="text"
          value={formData.shortName}
          onChange={(e) => handleInputChange('shortName', e.target.value)}
          style={errors.shortName ? errorInputStyle : inputStyle}
          placeholder="Kurzname (optional)"
          disabled={loading}
        />
        {errors.shortName && <div style={errorStyle}>{errors.shortName}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Beschreibung</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          style={{
            ...inputStyle,
            ...(errors.description ? { borderColor: '#ef4444' } : {}),
            minHeight: '80px',
            resize: 'vertical' as const
          }}
          placeholder="Beschreibung (optional)"
          disabled={loading}
        />
        {errors.description && <div style={errorStyle}>{errors.description}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Level *</label>
        <input
          type="number"
          min="1"
          max="10"
          value={formData.level}
          onChange={(e) => handleInputChange('level', parseInt(e.target.value) || 1)}
          style={errors.level ? errorInputStyle : inputStyle}
          disabled={loading}
        />
        {errors.level && <div style={errorStyle}>{errors.level}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Sportart *</label>
        <select
          value={formData.sportId}
          onChange={(e) => handleInputChange('sportId', parseInt(e.target.value))}
          style={errors.sportId ? { ...selectStyle, borderColor: '#ef4444' } : selectStyle}
          disabled={loading}
        >
          <option value={0}>Sportart auswählen</option>
          {sports.map(sport => (
            <option key={sport.id} value={sport.id}>
              {sport.icon ? `${sport.icon} ` : ''}{sport.name}
            </option>
          ))}
        </select>
        {errors.sportId && <div style={errorStyle}>{errors.sportId}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Land *</label>
        <select
          value={formData.countryId}
          onChange={(e) => handleInputChange('countryId', parseInt(e.target.value))}
          style={errors.countryId ? { ...selectStyle, borderColor: '#ef4444' } : selectStyle}
          disabled={loading}
        >
          <option value={0}>Land auswählen</option>
          {countries.map(country => (
            <option key={country.id} value={country.id}>
              {country.flag ? `${country.flag} ` : ''}{country.name}
            </option>
          ))}
        </select>
        {errors.countryId && <div style={errorStyle}>{errors.countryId}</div>}
      </div>

      {league && (
        <div style={fieldStyle}>
          <div style={checkboxContainerStyle}>
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              style={checkboxStyle}
              disabled={loading}
            />
            <label htmlFor="isActive" style={{ ...labelStyle, marginBottom: 0 }}>
              Aktiv
            </label>
          </div>
        </div>
      )}

      <div style={buttonContainerStyle}>
        <button
          type="button"
          onClick={onCancel}
          style={secondaryButtonStyle}
          disabled={loading}
        >
          Abbrechen
        </button>
        <button
          type="submit"
          style={primaryButtonStyle}
          disabled={loading}
        >
          {loading ? 'Speichern...' : (league ? 'Aktualisieren' : 'Erstellen')}
        </button>
      </div>
    </form>
  );
};

export default LeagueForm;
